import { useState } from "react"
import { Trash2, Download, Loader2, AlertTriangle } from "lucide-react"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import hive, { triggerDownload } from "@/lib/api/hive"
import { Analytics } from "@/lib/analytics"

interface DeletePortfolioDialogProps {
	portfolioId: string
	portfolioName: string
	positionCount: number
	token: string
	onDeleted: () => void
}

export function DeletePortfolioDialog({
	portfolioId,
	portfolioName,
	positionCount,
	token,
	onDeleted,
}: DeletePortfolioDialogProps) {
	const [open, setOpen] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [isExporting, setIsExporting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleExport = async () => {
		setIsExporting(true)
		try {
			const blob = await hive.downloadTransactionsCSV(portfolioId, token)
			const safeName = portfolioName.replace(/[^a-zA-Z0-9_-]/g, "_") || "portfolio"
			triggerDownload(blob, `beediv-transactions-${safeName}.csv`)
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Export failed")
		} finally {
			setIsExporting(false)
		}
	}

	const handleDelete = async () => {
		setIsDeleting(true)
		setError(null)
		try {
			await hive.deletePortfolio(portfolioId, token)
			Analytics.capture("Portfolio Deleted")
			setOpen(false)
			onDeleted()
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to delete portfolio")
		} finally {
			setIsDeleting(false)
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				setError(null)
			}}
		>
			<DialogTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
					title="Delete portfolio"
				>
					<Trash2 className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[440px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-red-500" />
						Delete "{portfolioName}"
					</DialogTitle>
					<DialogDescription>
						{positionCount > 0
							? `This portfolio has ${positionCount} position${positionCount !== 1 ? "s" : ""} with transaction history. This action is permanent and cannot be undone.`
							: "This action is permanent and cannot be undone."}
					</DialogDescription>
				</DialogHeader>

				{positionCount > 0 && (
					<div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 p-4">
						<p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-3">
							Export your transaction history before deleting — it can't be recovered afterward.
						</p>
						<Button
							variant="outline"
							size="sm"
							onClick={handleExport}
							disabled={isExporting || isDeleting}
							className="gap-2 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
						>
							{isExporting ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Download className="h-4 w-4" />
							)}
							{isExporting ? "Exporting..." : "Export Transactions CSV"}
						</Button>
					</div>
				)}

				{error && <p className="text-sm text-red-500 font-medium">{error}</p>}

				<DialogFooter>
					<Button variant="outline" onClick={() => setOpen(false)} disabled={isDeleting}>
						Cancel
					</Button>
					<Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
						{isDeleting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Deleting...
							</>
						) : positionCount > 0 ? (
							"Delete Without Backup"
						) : (
							"Delete Portfolio"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
